import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { AfishaMoreMenu } from "./AfishaMoreMenu";
import { AfishaSearchBar } from "./AfishaSearchBar";
import {
  AfishaHomeSearchBridgeProvider,
  useAfishaHomeSearchBridge,
} from "./afishaHomeSearchBridge";
import { MessageArea } from "../../common/MessageArea";
import { InstallShortcutBanner } from "./InstallShortcutBanner";
import { ThemeToggle } from "./ThemeToggle";
import {
  AfishaTheme,
  getNextTheme,
  getStoredTheme,
  getSystemTheme,
  hasStoredTheme,
  saveTheme,
} from "../../../utils/afishaTheme";
import { QueryService } from "../../../services/QueryService";
import "./AfishaPage.scss";

const APP_TITLE = "КудаВГород";

const AfishaLayoutBody = () => {
  const { pathname } = useLocation();
  const {
    bridge: homeSearch,
    searchPanelOpen,
    setSearchPanelOpen,
  } = useAfishaHomeSearchBridge();
  const isHome = pathname === "/";
  const [theme, setTheme] = useState<AfishaTheme>(() => getStoredTheme());

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = getNextTheme(prev);
      saveTheme(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (hasStoredTheme()) {
      return;
    }

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setTheme(getSystemTheme());

    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const titles: Record<string, string> = {
      "/about": `О проекте — ${APP_TITLE}`,
      "/channels": `Каналы — ${APP_TITLE}`,
    };
    document.title = titles[pathname] ?? APP_TITLE;
  }, [pathname]);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute("content", theme === "dark" ? "#121214" : "#7e34bb");
    }
  }, [theme]);

  useEffect(() => {
    if (pathname === "/" || pathname.startsWith("/channels")) {
      return;
    }

    void QueryService.postAfishaQuery("/api/channels/track");
  }, [pathname]);

  useEffect(() => {
    setSearchPanelOpen(false);
  }, [pathname, setSearchPanelOpen]);

  const toggleHeaderSearch = useCallback(() => {
    if (searchPanelOpen) {
      setSearchPanelOpen(false);
      homeSearch?.clearSearch();
      return;
    }
    setSearchPanelOpen(true);
  }, [searchPanelOpen, setSearchPanelOpen, homeSearch]);

  return (
    <div className={`afisha afisha--${theme}`}>
      <MessageArea />
      <header className="afisha__topbar">
        <NavLink to="/" className="afisha__logo-link" end>
          <p className="afisha__logo">
            <span className="afisha__logo-line">
              <span className="afisha__logo-accent">К</span>уда
              <span className="afisha__logo-accent">
                <span>В</span>
              </span>
              <span className="afisha__logo-accent"></span>город
            </span>
          </p>
        </NavLink>

        <div className="afisha__topbar-actions">
          <AfishaMoreMenu />
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          {isHome && homeSearch && (
            <button
              type="button"
              className={
                "afisha__topbar-icon-btn" +
                (searchPanelOpen ? " afisha__topbar-icon-btn--active" : "")
              }
              aria-pressed={searchPanelOpen}
              aria-label={searchPanelOpen ? "Закрыть поиск" : "Поиск"}
              title={searchPanelOpen ? "Закрыть поиск" : "Поиск"}
              onClick={toggleHeaderSearch}
            >
              <i
                className={searchPanelOpen ? "bi bi-x-lg" : "bi bi-search"}
                aria-hidden
              />
            </button>
          )}
        </div>
      </header>

      {isHome && searchPanelOpen && homeSearch && (
        <AfishaSearchBar
          key={homeSearch.resetKey}
          autoFocus
          loading={homeSearch.searchLoading}
          onSearch={homeSearch.onSearch}
        />
      )}

      <InstallShortcutBanner />

      <Outlet />
    </div>
  );
};

export const AfishaLayout = () => (
  <AfishaHomeSearchBridgeProvider>
    <AfishaLayoutBody />
  </AfishaHomeSearchBridgeProvider>
);
